<template>
  <rmp-base-modal
    v-model="isOpen"
    extra-classes="RedirectSettingsModal"
  >
    <template #modal-header>
      <h2 class="u-text-center">
        Redirect on completion
      </h2>
    </template>
    <template #modal-body>
      <div class="radio-button-group">
        <rmp-radio-button
          v-model="redirectSettingAnswer"
          label="None"
          name="redirectSetting"
          class-name="RadioButton"
          value="none"
          :hint="{
            type: 'info',
            hint: 'When the student submits their application they will see the defined success message'
          }"
        />
        <rmp-radio-button
          v-model="redirectSettingAnswer"
          label="External Redirect"
          name="redirectSetting"
          class-name="RadioButton"
          value="redirecting_to_another_url"
          :hint="{
            type: 'info',
            hint: 'When the student submits their application, they will be automatically redirected to the provided url'
          }"
        />
        <div
          v-if="redirectSettingAnswer === 'redirecting_to_another_url'"
          class="Notice"
        >
          <h3>Enter URL to redirect to</h3>
          <rmp-input
            v-model="redirectURL"
            placeholder="e.g. https://rmp-connect.com"
            :error="validateStatus($v.redirectURL, 'Redirect URL')"
            :manual-validate="validateAll"
          />
        </div>
        <rmp-radio-button
          v-model="redirectSettingAnswer"
          label="Redirect to another web form"
          name="redirectSetting"
          class-name="RadioButton"
          value="redirecting_to_another_web_form"
          :hint="{
            type: 'info',
            hint: `When the student submits their application, they will be automatically redirected to the selected web form with all student question answers passed onto the next webform`
          }"
        />
        <div
          v-if="redirectSettingAnswer === 'redirecting_to_another_web_form'"
          class="Notice"
        >
          <h3>Select form to redirect to</h3>
          <rmp-autocomplete-select
            v-model="selectedWebform"
            placeholder="Select form"
            :error="validateStatus($v.selectedWebform, 'Form')"
            :manual-validate="validateAll"
            :items="Object.values(webformsList)"
          />
        </div>
      </div>
    </template>
    <template #modal-footer>
      <div class="u-floatLeft">
        <button
          class="Button Button--green-outline"
          @click="isOpen = false"
        >
          Cancel
        </button>
      </div>
      <div class="u-floatRight">
        <button
          :disabled="saveButtonDisabled"
          class="Button Button--green"
          @click="save"
        >
          Save
        </button>
      </div>
    </template>
  </rmp-base-modal>
</template>

<script>
import { mapActions } from 'vuex';
import { requiredIf, url } from 'vuelidate/lib/validators';
import RmpBaseModal from '@rmpenterprise/base-modal';
import RmpRadioButton from '@rmpenterprise/form-radio-button';
import RmpInput from '@rmpenterprise/form-input';
import RmpAutocompleteSelect from '@rmpenterprise/form-autocomplete-select';
import ModalActionsMixin from './mixins/modal-actions-mixin';
import ValidationMixin from '../../../mixins/validation-mixin';

export default {
  components: {
    RmpBaseModal,
    RmpRadioButton,
    RmpInput,
    RmpAutocompleteSelect,
  },
  mixins: [ModalActionsMixin, ValidationMixin],
  data() {
    return {
      redirectSettingAnswer: 'none',
      webformsList: {},
      redirectURL: null,
      selectedWebform: null,
      validateAll: false,
      saveButtonDisabled: false,
    };
  },
  computed: {
    selectedWebformID() {
      if (this.selectedWebform) {
        const id = Object.keys(this.webformsList).find((key) => this.webformsList[key] === this.selectedWebform); // get webform ID from selected webform name
        return Number(id); // convert from string to int
      }

      return null;
    },
  },
  mounted() {
    this.getWebforms()
      .then((response) => {
        this.webformsList = response.data;
      });

    this.getSettings();
  },
  methods: {
    ...mapActions('events', ['getWebforms', 'getRedirectSettings', 'saveRedirectSettings']),
    getSettings() {
      this.getRedirectSettings(this.$route.params.id)
        .then((response) => {
          this.redirectSettingAnswer = response.data.type;

          if (response.data.type === 'redirecting_to_another_url') {
            this.redirectURL = response.data.value;
          } else if (response.data.type === 'redirecting_to_another_web_form') {
            this.selectedWebform = this.webformsList[response.data.value];
          }

          this.$emit('redirect-settings:responseSuccess');
        });
    },
    save() {
      this.validateAll = true;
      if (this.$v.$invalid) {
        return;
      }
      this.validateAll = false;

      this.saveButtonDisabled = true;

      let payload;
      switch (this.redirectSettingAnswer) {
        case 'redirecting_to_another_url':
          payload = {
            redirect_to_url: this.redirectURL,
          };
          break;
        case 'redirecting_to_another_web_form':
          payload = {
            web_form_id: this.selectedWebformID,
          };
          break;
        case 'none':
        default:
          payload = {
            none: true,
          };
          break;
      }

      this.saveRedirectSettings({ eventID: this.$route.params.id, payload })
        .then((response) => {
          this.isOpen = false;
          this.$bus.$emit('displayMsg', {
            text: response.data.message,
            type: 'success',
            duration: 5000,
          });
        })
        .catch((error) => {
          this.$bus.$emit('displayMsg', {
            text: error.data.message,
            type: 'error',
            duration: 5000,
          });
        })
        .finally(() => {
          this.saveButtonDisabled = false;
        });
    },
  },
  validations() {
    if (this.redirectSettingAnswer === 'redirecting_to_another_url') {
      return {
        redirectURL: {
          required: requiredIf(() => {
            return this.redirectSettingAnswer === 'redirecting_to_another_url';
          }),
          url,
        },
      };
    } if (this.redirectSettingAnswer === 'redirecting_to_another_web_form') {
      return {
        selectedWebform: {
          required: requiredIf(() => {
            return this.redirectSettingAnswer === 'redirecting_to_another_web_form';
          }),
        },
      };
    }

    return {};
  },
};
</script>

<style lang="less" scoped>
  .RadioButton {
    width: 100%;
    margin-bottom: 20px;
  }

  .Notice {
    border: 1px solid #CCCCCC;
    border-radius: 3px;
    background: #F7F7F7;
    padding-bottom: 0;
  }
</style>
